// GDPR "Export my data" — pure, unit-tested JSON shaping.
//
// This module assembles a user's own data into human-readable JSON files for a
// password-protected ZIP export (GDPR Art. 20). It is FULLY governed by
// docs/GDPR-DATA-EXPORT.md — keep that doc, SCHEMA text below, and these
// builders in lock-step (see the Maintenance Rule in the doc).
//
// TWO HARD RULES, both enforced by redactSecrets() + tests:
//   1. NEVER export secrets (recovery key/code derivation secret, TOTP secret /
//      otpauth_url, passkey public key / raw credential material, tokens / DPoP
//      / split-key / DEK material, any password).
//   2. NEVER export derived/generated data (counts, expanded/synthesised URLs,
//      client-computed status/lifetime/live badges). Export raw stored values.

import type {
  Identity, FieldInstance, Profile, Exposure, Subscription, ShareLink, Contact, AuthMethod,
} from '../api/types.js';

// ---- forbidden-key guard ---------------------------------------------------

// Keys that must NEVER appear anywhere in an exported JSON object. Matched
// case-insensitively against every key at every depth by redactSecrets().
export const FORBIDDEN_KEYS: string[] = [
  // recovery
  'recovery_key', 'recoverykey', 'recovery_code', 'recoverycode',
  // code / join-code derivation secret
  'derivation_key', 'derivation_secret', 'seed', 'join_secret',
  // TOTP
  'secret', 'otpauth_url', 'otpauth', 'totp_secret',
  // passkey / WebAuthn key material
  'public_key', 'publickey', 'credential_id', 'credentialid', 'attestation',
  'private_key', 'privatekey', 'sign_count', 'signcount',
  // session / transport / split-key material
  'token', 'access_token', 'refresh_token', 'dpop', 'dpop_key',
  'subscriber_secret', 'subscribersecret', 'shared_secret', 'sharedsecret',
  'subscriber_secret_wrapped', 'dek', 'kdf', 'ephemeral_public_jwk', 'key_material',
  // passwords
  'password', 'passphrase', 'pin',
];

const FORBIDDEN_SET = new Set(FORBIDDEN_KEYS.map((k) => k.toLowerCase()));

// Recursively strip any forbidden key from a value. Returns a deep-cleaned copy.
// This is a defensive backstop: builders must not add secrets in the first
// place, but redactSecrets guarantees none leak even if an upstream shape gains
// a secret field later (Maintenance Rule).
export function redactSecrets<T>(value: T): T {
  if (Array.isArray(value)) return value.map((v) => redactSecrets(v)) as unknown as T;
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (FORBIDDEN_SET.has(k.toLowerCase())) continue;
      out[k] = redactSecrets(v);
    }
    return out as unknown as T;
  }
  return value;
}

// Assert (throws) that a value contains no forbidden key at any depth. Used by
// the export path as a last line of defence before bytes are written.
export function assertNoSecrets(value: unknown, path = 'root'): void {
  if (Array.isArray(value)) {
    value.forEach((v, i) => assertNoSecrets(v, `${path}[${i}]`));
    return;
  }
  if (value && typeof value === 'object') {
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (FORBIDDEN_SET.has(k.toLowerCase())) {
        throw new Error(`export secret-leak: forbidden key "${k}" at ${path}`);
      }
      assertNoSecrets(v, `${path}.${k}`);
    }
  }
}

// ---- filename --------------------------------------------------------------

// VirtualID_DataExport_<username>_<YYYYMMDDHHMMSS>_utc.zip
// username sanitised to [A-Za-z0-9_-] (fallback "user"); timestamp is UTC.
export function exportFileName(username: string, date: Date): string {
  const user = (username || '').replace(/[^A-Za-z0-9_-]/g, '') || 'user';
  const p2 = (n: number) => String(n).padStart(2, '0');
  const stamp = `${date.getUTCFullYear()}${p2(date.getUTCMonth() + 1)}${p2(date.getUTCDate())}`
    + `${p2(date.getUTCHours())}${p2(date.getUTCMinutes())}${p2(date.getUTCSeconds())}`;
  return `VirtualID_DataExport_${user}_${stamp}_utc.zip`;
}

// ---- per-file JSON builders ------------------------------------------------

export interface IdentityJson {
  identity: {
    id?: string; username?: string; handle?: string; display_name?: string;
    given_name?: string; family_name?: string; type?: string; region?: string;
    status?: string; created_at?: string; updated_at?: string;
  };
  fields: { id?: string; type?: string; label?: string; value?: string; created_at?: string; updated_at?: string }[];
}

// identity.json — account/common data + the user's own common field instances.
// given_name/family_name are pulled from the raw field pool (they are stored
// field values, not derived).
export function buildIdentityJson(me: Identity, fields: FieldInstance[]): IdentityJson {
  const fieldVal = (t: string) =>
    (fields.find((f) => (f.type || f.field_type_key) === t)?.value || '') || undefined;
  return {
    identity: {
      id: me.id,
      username: me.username,
      handle: me.handle,
      display_name: me.display_name,
      given_name: fieldVal('given_name'),
      family_name: fieldVal('family_name'),
      type: me.type,
      region: me.region,
      status: me.status,
      created_at: me.created_at,
      updated_at: me.updated_at,
    },
    fields: fields.map((f) => ({
      id: f.id,
      type: f.type || f.field_type_key,
      label: f.label,
      value: f.value,
      created_at: f.created_at,
      updated_at: f.updated_at,
    })),
  };
}

export interface ProfileJson {
  id: string; name: string; description?: string; icon?: string; color?: string;
  is_public?: boolean; enabled?: boolean; avatar_url?: string | null;
  created_at?: string; updated_at?: string;
  exposed_field_ids: string[];
  subscribers: { subscriber_id?: string; status?: string; paused?: boolean; added_at?: string; labels?: string[] }[];
  share_links: {
    id: string; code?: string; alias?: string; label?: string; max_uses?: number | null;
    expires_at?: string | null; revoked_at?: string | null; disabled_at?: string | null;
    created_at?: string; medium?: string; notes?: string; approval_mode?: string; kind?: string;
  }[];
}

// profile_<id>.json — one per profile. exposed_field_ids reference field ids
// ONLY (values live in identity.json). subscribers are raw ids + non-derived
// metadata. share_links carry only non-secret/non-derived attributes.
export function buildProfileJson(
  profile: Profile,
  exposures: Exposure[],
  subscribers: Subscription[],
  shareLinks: ShareLink[],
): ProfileJson {
  const exposed = exposures.filter((e) => e.exposed).map((e) => e.field_id);
  const subs = subscribers
    .filter((s) => s.profile_id === profile.id)
    .map((s) => ({
      subscriber_id: s.subscriber_id,
      status: s.status,
      paused: s.paused,
      added_at: s.added_at,
      labels: s.labels && s.labels.length ? s.labels : undefined,
    }));
  const links = shareLinks
    .filter((l) => l.profile_id === profile.id)
    .map((l) => ({
      id: l.id,
      code: l.code,
      alias: l.alias,
      label: l.label,
      max_uses: l.max_uses,
      expires_at: l.expires_at,
      revoked_at: l.revoked_at,
      disabled_at: l.disabled_at,
      created_at: l.created_at,
      medium: l.medium,
      notes: l.notes,
      approval_mode: l.approval_mode,
      kind: l.kind,
    }));
  return {
    id: profile.id,
    name: profile.name,
    description: profile.description,
    icon: profile.icon,
    color: profile.color,
    is_public: profile.is_public,
    enabled: profile.enabled,
    avatar_url: profile.avatar_url ?? undefined,
    created_at: profile.created_at,
    updated_at: profile.updated_at,
    exposed_field_ids: exposed,
    subscribers: subs,
    share_links: links,
  };
}

export interface ContactJson {
  subscription_id: string; owner_id?: string; owner_handle?: string;
  profile_id?: string; profile_name?: string; status?: string; sync_state?: string;
  added_at?: string; last_synced_at?: string; content_changed_at?: string;
  labels?: string[]; notes?: { id?: string; body?: string; created_at?: string; updated_at?: string }[];
  fields: { key: string; type: string; label: string; value: string; syncing: boolean; order?: number }[];
}

// contacts.json — people I subscribe to. Fields DO embed the synced values plus
// the per-field `syncing` flag (raw wire data the subscriber legitimately sees).
export function buildContactsJson(contacts: Contact[]): ContactJson[] {
  return contacts.map((c) => ({
    subscription_id: c.subscription_id || c.id || '',
    owner_id: c.owner_id,
    owner_handle: c.owner_handle,
    profile_id: c.profile_id,
    profile_name: c.profile_name,
    status: c.status,
    sync_state: c.sync_state,
    added_at: c.added_at,
    last_synced_at: c.last_synced_at,
    content_changed_at: c.content_changed_at,
    labels: c.labels && c.labels.length ? c.labels : undefined,
    notes: (c.notes || []).map((n) => ({ id: n.id, body: n.body, created_at: n.created_at, updated_at: n.updated_at })),
    fields: (c.fields || []).map((f) => ({
      key: f.key, type: f.type, label: f.label, value: f.value, syncing: f.syncing, order: f.order,
    })),
  }));
}

export interface SettingsJson {
  authenticators: { id: string; label?: string; confirmed?: boolean; created_at?: string; last_used_at?: string }[];
  passkeys: { id: string; label?: string; kind?: string; transports?: string[]; created_at?: string; last_used_at?: string }[];
}

// settings.json — security metadata only, secrets stripped. No TOTP secret /
// otpauth_url, no passkey public key / credential material.
export function buildSettingsJson(authenticators: AuthMethod[], passkeys: AuthMethod[]): SettingsJson {
  return {
    authenticators: authenticators.map((a) => ({
      id: a.id, label: a.label, confirmed: a.confirmed,
      created_at: a.created_at, last_used_at: a.last_used_at,
    })),
    passkeys: passkeys.map((p) => ({
      id: p.id, label: p.label, kind: p.kind,
      transports: p.transports && p.transports.length ? p.transports : undefined,
      created_at: p.created_at, last_used_at: p.last_used_at,
    })),
  };
}

export interface ActivityEvent {
  type: 'subscribed' | 'pending' | 'link_created' | 'link_revoked';
  at?: string; subject_id?: string; profile_id?: string;
}

// activity.json — raw recent-activity EVENTS (not rendered HTML). Currently
// client-derived from subscriptions + share-links (no /activity endpoint in v1);
// we export the underlying event tuples with raw subject ids + timestamps.
export function buildActivityJson(subscribers: Subscription[], shareLinks: ShareLink[]): ActivityEvent[] {
  const events: ActivityEvent[] = [];
  for (const s of subscribers) {
    if (s.status === 'pending') {
      events.push({ type: 'pending', at: s.added_at, subject_id: s.subscriber_id, profile_id: s.profile_id });
    } else if (s.status === 'active') {
      events.push({ type: 'subscribed', at: s.added_at, subject_id: s.subscriber_id, profile_id: s.profile_id });
    }
  }
  for (const l of shareLinks) {
    events.push({ type: 'link_created', at: l.created_at, subject_id: l.id, profile_id: l.profile_id });
    if (l.revoked_at) {
      events.push({ type: 'link_revoked', at: l.revoked_at, subject_id: l.id, profile_id: l.profile_id });
    }
  }
  return events;
}

// ---- docs (README + SCHEMA), identical .txt/.md content --------------------

// buildReadme() — plain-language description. Kept in sync with the doc.
export function buildReadme(): string {
  return [
    'VirtualID — Data Export',
    '=======================',
    '',
    'This archive is your personal data export (GDPR right to data portability,',
    'Article 20). It is a password-protected ZIP of human-readable JSON files.',
    '',
    'Opening this archive',
    '--------------------',
    'It uses the legacy ZipCrypto method so Windows Explorer can open it with your',
    'password ("Extract All"). ZipCrypto is weaker than AES; for highly sensitive',
    'storage, re-pack with 7-Zip/AES. The password is the one you typed at export',
    'time and cannot be recovered.',
    '',
    'What is included',
    '----------------',
    '- identity.json    : your account (id, username, handle, display name, given/',
    '                     family name, type, region, status, timestamps) and your',
    '                     own common field instances (id, type, label, value, times).',
    '- profile_<id>.json: one file per profile. Profile metadata, the field IDs',
    '                     exposed on it (by reference only — values live in',
    '                     identity.json), its subscribers (raw ids + status/paused/',
    '                     added_at/labels), and its share links (non-secret fields).',
    '- contacts.json    : the people you subscribe to, with each synced field value',
    '                     and its "syncing" flag, plus your labels and notes.',
    '- settings.json    : your authenticators (TOTP) and passkeys — metadata only',
    '                     (labels + timestamps). NO secrets or key material.',
    '- activity.json    : recent activity events (subscribed / pending / link',
    '                     created / link revoked) with raw subject ids + timestamps.',
    '- SCHEMA.txt/.md   : a field-by-field description of every file above.',
    '',
    'What is deliberately NOT included',
    '---------------------------------',
    'Secrets: your recovery key/code and its derivation secret, TOTP secrets and',
    'otpauth URLs, passkey public keys / credential material, session tokens, DPoP',
    'keys, split-key/DEK material, and any password are NEVER exported.',
    '',
    'Derived data: computed counts, expanded/synthesised links, and client-side',
    'status/lifetime/live badges are NOT exported. Raw stored ids/aliases/codes are',
    'included so you can re-derive links yourself.',
    '',
  ].join('\n');
}

// buildSchema() — field-by-field contract for every file. Kept in sync w/ doc.
export function buildSchema(): string {
  return [
    'VirtualID — Data Export SCHEMA',
    '==============================',
    'Type notation: string, bool, number, string[], object[], ISO8601 = RFC3339',
    'timestamp string. "optional" means the key may be absent/empty.',
    '',
    'identity.json',
    '-------------',
    'identity.id            string    account id',
    'identity.username      string    login username (optional)',
    'identity.handle        string    public handle (optional)',
    'identity.display_name  string    display name (optional)',
    'identity.given_name    string    from your given_name field value (optional)',
    'identity.family_name   string    from your family_name field value (optional)',
    'identity.type          string    "individual" | "business" (optional)',
    'identity.region        string    "eu" | "us" (optional)',
    'identity.status        string    account status (optional)',
    'identity.created_at    ISO8601   account creation time (optional)',
    'identity.updated_at    ISO8601   last account update (optional)',
    'fields[]               object[]  your common field instances',
    'fields[].id            string    field instance id',
    'fields[].type          string    field type key (e.g. email, phone)',
    'fields[].label         string    your label (optional)',
    'fields[].value         string    the stored value (optional)',
    'fields[].created_at    ISO8601   (optional)',
    'fields[].updated_at    ISO8601   (optional)',
    '',
    'profile_<id>.json (one per profile)',
    '-----------------------------------',
    'id                     string    profile id',
    'name                   string    profile name',
    'description            string    (optional)',
    'icon                   string    (optional)',
    'color                  string    (optional)',
    'is_public              bool      (optional)',
    'enabled                bool      (optional)',
    'avatar_url             string    (optional, may be null)',
    'created_at             ISO8601   (optional)',
    'updated_at             ISO8601   (optional)',
    'exposed_field_ids      string[]  field IDs exposed on this profile (ref only,',
    '                                 no values — see identity.json for values)',
    'subscribers[]          object[]  raw subscriber rows',
    'subscribers[].subscriber_id string  subscriber id (build id-link yourself)',
    'subscribers[].status   string    "pending" | "active" | "revoked" (optional)',
    'subscribers[].paused   bool      (optional)',
    'subscribers[].added_at ISO8601   when they subscribed (optional)',
    'subscribers[].labels   string[]  your labels for them (optional)',
    'share_links[]          object[]  invite/QR links for this profile',
    'share_links[].id       string    link id',
    'share_links[].code     string    invite code (optional)',
    'share_links[].alias    string    public alias (optional)',
    'share_links[].label    string    (optional)',
    'share_links[].max_uses number    max redemptions (optional, may be null)',
    'share_links[].expires_at ISO8601 (optional, may be null)',
    'share_links[].revoked_at ISO8601 (optional, may be null)',
    'share_links[].disabled_at ISO8601 (optional, may be null)',
    'share_links[].created_at ISO8601 (optional)',
    'share_links[].medium   string    where shared, e.g. qr_screen (optional)',
    'share_links[].notes    string    (optional)',
    'share_links[].approval_mode string "explicit" | "auto" (optional)',
    'share_links[].kind     string    "qr_link" | "invitation_code" (optional)',
    '',
    'contacts.json',
    '-------------',
    '[]                     object[]  one entry per contact you subscribe to',
    '[].subscription_id     string    your subscription id',
    '[].owner_id            string    contact owner id (optional)',
    '[].owner_handle        string    contact owner handle (optional)',
    '[].profile_id          string    owner profile you subscribed to (optional)',
    '[].profile_name        string    (optional)',
    '[].status              string    (optional)',
    '[].sync_state          string    (optional)',
    '[].added_at            ISO8601   created / subscribed at (optional)',
    '[].last_synced_at      ISO8601   (optional)',
    '[].content_changed_at  ISO8601   last content change (optional)',
    '[].labels              string[]  your labels (optional)',
    '[].notes[]             object[]  your notes {id,body,created_at,updated_at}',
    '[].fields[]            object[]  the synced field snapshot',
    '[].fields[].key        string    field key',
    '[].fields[].type       string    field type',
    '[].fields[].label      string    label',
    '[].fields[].value      string    the synced value',
    '[].fields[].syncing    bool      false = owner stopped exposing it (kept, stale)',
    '[].fields[].order      number    owner display order (optional)',
    '',
    'settings.json',
    '-------------',
    'authenticators[]       object[]  TOTP authenticators (NO secret/otpauth_url)',
    'authenticators[].id            string   authenticator id',
    'authenticators[].label         string   (optional)',
    'authenticators[].confirmed     bool     (optional)',
    'authenticators[].created_at    ISO8601  (optional)',
    'authenticators[].last_used_at  ISO8601  (optional)',
    'passkeys[]             object[]  passkeys (NO public key / credential material)',
    'passkeys[].id          string    passkey id',
    'passkeys[].label       string    (optional)',
    'passkeys[].kind        string    (optional)',
    'passkeys[].transports  string[]  (optional)',
    'passkeys[].created_at  ISO8601   (optional)',
    'passkeys[].last_used_at ISO8601  (optional)',
    '',
    'activity.json',
    '-------------',
    '[]                     object[]  recent activity events (raw, not HTML)',
    '[].type                string    subscribed | pending | link_created | link_revoked',
    '[].at                  ISO8601   event time (optional)',
    '[].subject_id          string    subscriber id or share-link id (optional)',
    '[].profile_id          string    related profile id (optional)',
    '',
  ].join('\n');
}

// ---- assembly helper -------------------------------------------------------

// Strip undefined values so the JSON is clean (JSON.stringify drops them, but we
// serialise via a stable helper). Returns pretty-printed UTF-8-friendly JSON.
export function toJsonBytes(value: unknown): Uint8Array {
  const clean = redactSecrets(value);
  assertNoSecrets(clean);
  return new TextEncoder().encode(JSON.stringify(clean, null, 2) + '\n');
}
